import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Play, CheckCircle2, Linkedin } from "lucide-react";
import { mediaUrl } from "../lib/api";
import { videoEmbedUrl } from "../lib/utils";

const FloatingNumbers = () => (
  <>
    {["£", "%", "7", "3", "9", "+", "=", "5"].map((c, i) => (
      <span key={i} className="absolute font-display font-extrabold text-white/10 select-none"
        style={{ left: `${(i * 37) % 90}%`, top: `${(i * 53) % 85}%`, fontSize: `${1.2 + (i % 3) * 0.6}rem` }}>{c}</span>
    ))}
  </>
);

export const FounderSection = ({ founder, accent = "orange" }) => {
  const [playing, setPlaying] = useState(false);
  if (!founder || (!founder.name && !founder.bio)) return null;

  const A = accent === "teal"
    ? { text: "text-teal", bg: "bg-teal", bgHover: "hover:bg-teal-dark", shadow: "hover:shadow-teal/30" }
    : { text: "text-orange", bg: "bg-orange", bgHover: "hover:bg-orange-dark", shadow: "hover:shadow-orange/30" };

  const uploaded = founder.video_upload;
  const embed = videoEmbedUrl(founder.video_url);
  const ytId = (founder.video_url || "").match(/(?:youtu\.be\/|[?&]v=|embed\/|shorts\/|live\/)([A-Za-z0-9_-]{11})/)?.[1];
  const poster = ytId ? `https://img.youtube.com/vi/${ytId}/maxresdefault.jpg`
    : (uploaded ? mediaUrl(`${uploaded}/poster`) : (founder.image ? mediaUrl(founder.image, 1000) : ""));
  const playUrl = embed.includes("youtube.com") ? `${embed}&autoplay=1` : `${embed}?autoplay=1`;
  const hasVideo = !!(uploaded || embed);
  const bioParas = (founder.bio || "").split(/\n\s*\n/).filter(Boolean);

  return (
    <section className="py-16 md:py-20 bg-slate-50" data-testid="founder-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="bg-white rounded-3xl border border-navy/5 shadow-lg p-7 md:p-10">
          <div className="w-40 sm:w-44 md:w-52 mx-auto sm:mx-0 sm:float-left sm:mr-8 mb-5 sm:mb-2">
            <div className="relative rounded-2xl overflow-hidden bg-navy aspect-[4/5] shadow-md">
              {hasVideo ? (
                playing ? (
                  uploaded ? (
                    <video data-testid="founder-video" className="absolute inset-0 w-full h-full object-cover bg-black" src={mediaUrl(uploaded)} poster={poster || undefined} autoPlay controls playsInline preload="auto" />
                  ) : (
                    <iframe data-testid="founder-video" className="absolute inset-0 w-full h-full" src={playUrl} title={`${founder.name} — video`} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                  )
                ) : (
                  <button type="button" data-testid="founder-video-play" onClick={() => setPlaying(true)} aria-label="Play founder video" className="group absolute inset-0 w-full h-full">
                    {poster ? <img loading="lazy" decoding="async" src={poster} alt={founder.name} className="w-full h-full object-cover" onError={(e) => { if (ytId && !e.target.dataset.fb) { e.target.dataset.fb = "1"; e.target.src = `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`; } }} /> : <div className="w-full h-full" />}
                    <span className="absolute inset-0 bg-navy/40 group-hover:bg-navy/30 transition-colors flex items-center justify-center">
                      <span className={`w-12 h-12 rounded-full ${A.bg} flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg`}><Play size={20} className="text-white ml-0.5" fill="white" /></span>
                    </span>
                  </button>
                )
              ) : founder.image ? (
                <img loading="lazy" decoding="async" src={mediaUrl(founder.image, 500)} alt={founder.name} className="absolute inset-0 w-full h-full object-cover" data-testid="founder-image" />
              ) : (
                <div className="absolute inset-0 numbers-pattern flex items-center justify-center overflow-hidden">
                  <FloatingNumbers />
                  <span className="relative font-display font-extrabold text-5xl text-white/25">{(founder.name || "P").charAt(0)}</span>
                </div>
              )}
            </div>
          </div>

          <span className={`inline-flex items-center gap-2 ${A.text} font-bold text-xs tracking-widest uppercase mb-2`}>{founder.eyebrow}</span>
          <h2 className="font-display font-extrabold text-2xl md:text-3xl text-navy leading-tight">{founder.name}</h2>
          {founder.role && <p className="text-sm font-semibold text-slate-400 mt-0.5 mb-4">{founder.role}</p>}
          <div className="space-y-3 text-sm md:text-base text-slate-500 leading-relaxed">
            {bioParas.map((p, i) => <p key={i}>{p}</p>)}
          </div>
          {founder.highlights?.length > 0 && (
            <ul className="mt-5 flex flex-wrap gap-2">
              {founder.highlights.map((h, i) => (
                <li key={i} className="inline-flex items-center gap-1.5 bg-slate-50 border border-navy/5 rounded-full pl-2.5 pr-3.5 py-1.5 text-xs font-semibold text-navy" data-testid={`founder-highlight-${i}`}>
                  <CheckCircle2 size={13} className={`${A.text} shrink-0`} /> <span>{h}</span>
                </li>
              ))}
            </ul>
          )}
          {(founder.cta_label || founder.linkedin) && (
            <div className="mt-6 flex flex-wrap items-center gap-3">
              {founder.cta_label && (
                <Link to={founder.cta_link || "/contact"} data-testid="founder-cta" className={`inline-flex items-center gap-2 ${A.bg} ${A.bgHover} text-white font-bold text-sm px-6 py-3 rounded-full transition-all hover:-translate-y-0.5 hover:shadow-lg ${A.shadow}`}>{founder.cta_label} <ArrowRight size={16} /></Link>
              )}
              {founder.linkedin && (
                <a href={founder.linkedin} target="_blank" rel="noopener noreferrer" data-testid="founder-linkedin" aria-label={`${founder.name} on LinkedIn`} className="inline-flex items-center gap-2 border-2 border-[#0A66C2] text-[#0A66C2] hover:bg-[#0A66C2] hover:text-white font-bold text-sm px-5 py-3 rounded-full transition-all hover:-translate-y-0.5">
                  <Linkedin size={16} className="fill-current" /> Connect on LinkedIn
                </a>
              )}
            </div>
          )}
          <div className="clear-both" />
        </div>
      </div>
    </section>
  );
};
